import { Show, Switch, Match, createSignal, createEffect, onCleanup } from 'solid-js'
import { A, useLocation, useNavigate, useSearchParams } from '@solidjs/router'
import { userStore } from '../../store'
import { CFLogo } from './Logo'
import { IconSearch, IconMenu } from './Icons'
import { UserMenu } from './UserMenu'
import { topBarNavLinks } from './navData'
import { hasActivePremium, getVipCreatorIds } from '/common/subscription-access'

export function TopBar(props: { onOpenDrawer: () => void }) {
  const [mobileSearch, setMobileSearch] = createSignal(false)
  const [searchValue, setSearchValue] = createSignal('')
  const nav = useNavigate()
  const location = useLocation()

  const [searchParams] = useSearchParams()

  createEffect(() => {
    if (location.pathname === '/directory') {
      setSearchValue(searchParams.q || '')
    }
  })

  // Close mobile search overlay on Escape
  createEffect(() => {
    if (!mobileSearch()) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileSearch(false)
    }
    document.addEventListener('keydown', handler)
    onCleanup(() => document.removeEventListener('keydown', handler))
  })

  const submitSearch = (value: string) => {
    const q = value.trim()
    if (!q) return
    setSearchValue(q)
    ;(window as any).__siteSearchQuery = q
    nav(`/directory?q=${encodeURIComponent(q)}`)
    setMobileSearch(false)
  }

  const clearSearch = () => {
    setSearchValue('')
    if (location.pathname === '/directory') {
      nav('/directory')
    }
  }
  const user = () => userStore()
  const loggedIn = () => user().loggedIn
  const tokens = () => user().profile?.totalAvailableTokens ?? 0
  const isPlatformPremium = () => hasActivePremium(user().aifansSubscriptions)
  const hasVip = () => getVipCreatorIds(user().aifansSubscriptions).length > 0

  const isActive = (route?: string) => {
    if (!route) return false
    if (route === '/') return location.pathname === '/'
    return location.pathname.startsWith(route)
  }

  const handleNavAction = (item: (typeof topBarNavLinks)[0]) => {
    if (item.action === 'directory') {
      userStore.directoryModal(true)
    }
  }

  return (
    <>
      <header
        class="cf-topbar"
        style={{ 'backdrop-filter': 'blur(20px)', '-webkit-backdrop-filter': 'blur(20px)' }}
      >
        {/* Mobile menu button */}
        <button class="cf-menu-btn" aria-label="Menu" onClick={props.onOpenDrawer}>
          <IconMenu size={22} />
        </button>

        {/* Logo */}
        <A href="/">
          <CFLogo size="md" />
        </A>

        {/* Search (tablet/desktop) */}
        <div class="cf-topbar-search">
          <IconSearch size={16} />
          <input
            type="text"
            placeholder="Search characters & creators..."
            value={searchValue()}
            onInput={(e) => setSearchValue(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitSearch(e.currentTarget.value)
            }}
          />
          <Show when={searchValue()}>
            <button
              class="absolute right-[10px] top-1/2 grid h-[22px] w-[22px] -translate-y-1/2 cursor-pointer place-items-center rounded-full border-none bg-[rgba(255,255,255,0.08)] text-[14px] leading-none text-[rgba(255,255,255,0.6)] transition-all duration-150 hover:bg-[rgba(255,60,172,0.25)] hover:text-white"
              onClick={clearSearch}
            >
              ×
            </button>
          </Show>
        </div>

        {/* Nav links (desktop only) */}
        <nav class="cf-topbar-nav">
          {topBarNavLinks.map((item) =>
            item.route ? (
              <A href={item.route} class={isActive(item.route) ? 'active' : ''}>
                {item.label}
              </A>
            ) : (
              <button
                class={item.action === 'directory' && user().showDirectory ? 'active' : ''}
                onClick={() => handleNavAction(item)}
              >
                {item.label}
              </button>
            )
          )}
        </nav>

        {/* Actions */}
        <div class="cf-topbar-actions">
          {/* Mobile search trigger */}
          <button
            class="cf-mobile-search-btn"
            aria-label="Search"
            onClick={() => setMobileSearch(true)}
          >
            <IconSearch size={18} />
          </button>

          {/* Tokens pill */}
          <button
            class="cf-pill-tokens"
            classList={{
              'cf-pill-tokens--member': loggedIn() && (isPlatformPremium() || hasVip()),
            }}
            aria-label="Buy tokens"
            onClick={() =>
              loggedIn() ? userStore.buyTokensModal(true) : userStore.loginModal(true)
            }
          >
            <Show when={loggedIn()} fallback={<>Tokens +</>}>
              {tokens()} <span style={{ opacity: '0.6' }}>+</span>
            </Show>
          </button>

          {/* Premium / Member button — visible logged-in AND logged-out.
              Logged-out: the Premium button is the primary CTA and opens the login modal. */}
          <Switch
            fallback={
              <button
                class="cf-btn-premium"
                aria-label="Get Premium – 70% off"
                onClick={() => (loggedIn() ? nav('/pricing') : userStore.loginModal(true))}
              >
                💎 Premium
                <span class="cf-btn-premium-off">70% OFF</span>
              </button>
            }
          >
            {/* Priority 1 — Premium Member (all-access platform premium).
                Non-interactive status badge per spec #202 — a <span>, not a link. */}
            <Match when={loggedIn() && isPlatformPremium()}>
              <span class="cf-btn-member" title="Premium member" aria-label="Premium member">
                <span class="cf-btn-member-gem">👑</span>
                <span>Premium</span>
                <span class="cf-btn-member-check">✓</span>
              </span>
            </Match>
            {/* Priority 2 — VIP (individual creator subscription) */}
            <Match when={loggedIn() && !isPlatformPremium() && hasVip()}>
              <span class="cf-btn-vip" aria-label="VIP subscriber" title="Active VIP subscription">
                <span class="cf-btn-vip-gem">👑</span>
                VIP
                <span class="cf-btn-vip-check">✓</span>
              </span>
            </Match>
          </Switch>

          {/* User menu (logged-in only) */}
          <Show when={loggedIn()}>
            <UserMenu />
          </Show>
        </div>
      </header>

      {/* Mobile search overlay */}
      <Show when={mobileSearch()}>
        <div
          class="cf-mobile-search-overlay"
          style={{ 'backdrop-filter': 'blur(6px)', '-webkit-backdrop-filter': 'blur(6px)' }}
          onClick={() => setMobileSearch(false)}
        >
          <div class="cf-mobile-search-bar" onClick={(e) => e.stopPropagation()}>
            <IconSearch size={16} stroke="var(--cf-muted)" />
            <input
              type="text"
              placeholder="Search characters & creators..."
              ref={(el) => {
                requestAnimationFrame(() => el.focus())
              }}
              value={searchValue()}
              onInput={(e) => setSearchValue(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitSearch(e.currentTarget.value)
              }}
            />
            <button class="cf-mobile-search-cancel" onClick={() => setMobileSearch(false)}>
              Cancel
            </button>
          </div>
        </div>
      </Show>
    </>
  )
}
